import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getForms } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Clock,
  CheckCircle2,
  Send,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const statusConfig = {
  draft: { label: "Entwurf", icon: Clock, className: "bg-slate-100 text-slate-600" },
  completed: { label: "Fertig", icon: CheckCircle2, className: "bg-emerald-50 text-emerald-600" },
  sent: { label: "Versendet", icon: Send, className: "bg-blue-50 text-blue-600" },
};

export default function DashboardPage() {
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, draft: 0, completed: 0, sent: 0 });

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const response = await getForms();
      const data = response.data;
      setForms(data.slice(0, 5));
      setStats({
        total: data.length,
        draft: data.filter((f) => f.status === "draft").length,
        completed: data.filter((f) => f.status === "completed").length,
        sent: data.filter((f) => f.status === "sent").length,
      });
    } catch (error) {
      toast.error("Fehler beim Laden der Formulare");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Übersicht Ihrer Produktinformationen</p>
        </div>
        <Link to="/form/new">
          <Button data-testid="new-form-button" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Neues Formular
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Gesamt</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Entwürfe</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.draft}</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Fertig</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.completed}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Versendet</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.sent}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Send className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Forms */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Letzte Formulare
            </CardTitle>
            <Link to="/archive">
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                Alle anzeigen
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Noch keine Formulare vorhanden</p>
              <Link to="/form/new">
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Erstes Formular erstellen
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {forms.map((form) => {
                const status = statusConfig[form.status] || statusConfig.draft;
                const StatusIcon = status.icon;
                return (
                  <Link
                    key={form.id}
                    to={`/form/${form.id}`}
                    data-testid={`form-item-${form.id}`}
                    className="flex items-center justify-between py-4 hover:bg-slate-50 -mx-6 px-6 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          Auftrag {form.order_number}
                        </p>
                        <p className="text-sm text-slate-500">
                          {format(new Date(form.created_at), "dd. MMM yyyy, HH:mm", { locale: de })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={`${status.className} gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
